/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-26 11:22:58                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-27 13:19:50                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * Services
  * Validator
  * QuotationForm: Create/edit quotation form
    * handleChange: handle field change event
    * useEffect: Initiate empty quotation 
*/

/* Imports */
import { useEffect, useState } from "react";
import { t } from "i18next";
/***/

/* Components */
import DateSelect from "../date/DateSelect";
import ContactSelect from "../contact/ContactSelect";
import CustomerSelect from "../customer/CustomerSelect";
import TechnicianSelect from "../technician/TechnicianSelect";
import QuotationPricingForm, { QuotationPricingFormValidator } from "./QuotationPricingForm";
import { embeddedFormChange } from "../../../services/utils/utils";
import FormDropdown from "../../form-dropdown/FormDropdown";
import CustomerInfoCard from "../customer/CustomerInfoCard";
import ContactInfoCard from "../contact/ContactInfoCard";
import TechnicianInfoCard from "../technician/TechnicianInfoCard";
/***/

/* Services */
import { validator } from "../../../services/form-validator/form-validator";
import { QuotationValidator } from "./quotation-validator";
/***/

/* Validator */
export const QuotationFormValidator = {
  customer: (e: unknown) => validator.isNotNull(e),
  contact: (e: unknown) => validator.isNotNull(e),
  technician: (e: unknown) => validator.isNotNull(e),
  duration: (e: unknown) => validator.isNumber(e),
  date: (e: unknown) => validator.isString(e),
  pricing: (e: unknown) => validator.isFormValid(e, QuotationPricingFormValidator)
};

const v = QuotationFormValidator; // shorthand
/***/

/**
* QuotationForm: Create/edit quotation form 
* @param data - Data to edit
* @param onChange - On change event, trigger on field change
* @param onSubmit - On submit event, trigger enter key down on field
* @param submitted - is form submitted
*/
export default function QuotationForm({
  data,
  onChange,
  onSubmit,
  submitted
}: {
  data?: EasyTech.Quotation,
  onChange: (name: string, value: any) => void,
  onSubmit: () => void,
  submitted?: boolean
}) {
  const [dataState, setDataState] = useState<EasyTech.Quotation>({
    ...data,
    pricing: data?.pricing || {}
  } as EasyTech.Quotation);

  const validateFormData = (formData: any): boolean => {
    if (!formData) return false;
    const validationErrors = QuotationValidator.validate(formData, t);
    return validationErrors.length === 0;
  };

  const handleSubmitWithValidation = () => {
    if (submitted && !validateFormData(data)) {
      console.warn("Validation échouée - champs obligatoires manquants");
      return;
    }
    onSubmit();
  };

  /**
  * useEffect: Initiate empty quotation
  */
  useEffect(() => {
    onChange("parts", []);
    onChange("services", []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /***/

  /**
  * handleChange: handle field change event
  * @param name - field name
  * @param value - param value 
  */
  const handleChange = (name: string, value: any) => {
    const tmp = {...dataState};
    tmp[name] = value;
    setDataState(tmp);
    onChange(name, value);
  };
  /***/

  return (
    <>
      <FormDropdown label={t("common:customer")}>
        <div className="field">
          <label>{t("common:customer")} *</label>
          <CustomerSelect onChange={(value) => handleChange("customer", value)}
                          value={dataState.customer}
                          className={`${(submitted && !validator.isFieldValid(data, "customer", v)) && "error"}`}/>
        </div>

        {dataState.customer &&
          <CustomerInfoCard customer={dataState.customer}/>
        }

        {dataState.customer && 
          <div className="field">
            <label>{t("common:contact")} *</label>
            <ContactSelect  onChange={(value) => handleChange("contact", value)}
                            value={dataState.contact}
                            customerId={dataState.customer.id}
                            className={`${(submitted && !validator.isFieldValid(data, "contact", v)) && "error"}`}/>
          </div>
        }

        {dataState.contact && 
          <ContactInfoCard contact={dataState.contact}/>
        }
      </FormDropdown>

      <FormDropdown label={t("common:technician")}>
        <div className="field">
          <label>{t("common:technician")} *</label>
          <TechnicianSelect onChange={(value) => handleChange("technician", value)}
                            value={dataState.technician}
                            className={`${(submitted && !validator.isFieldValid(data, "technician", v)) && "error"}`}/>
        </div>

        {dataState.technician &&
          <TechnicianInfoCard technician={dataState.technician}/>
        }
      </FormDropdown>

      <FormDropdown label={t("common:times")}>
        <div className="field">
          <label>{t("common:date.label")} *</label>
          <DateSelect value={dataState.date}
                      onChange={(value) => handleChange("date", value)}
                      className={`${(submitted && !validator.isFieldValid(data, "date", v)) && "error"}`}/>
        </div>

        <div className="field">
          <label>{t("common:duration")} *</label>
          <input  type="number"
                  value={dataState.duration}
                  placeholder="h"
                  min={0}
                  onChange={(value) => handleChange("duration", Number(value.target.value))}
                  className={`form-field ${(submitted && !validator.isFieldValid(data, "duration", v)) && "error"}`}/>
        </div>
      </FormDropdown>

      <FormDropdown label={t("common:pricing")}>
        <QuotationPricingForm data={dataState.pricing}
                              onChange={(name, value) => (
                                embeddedFormChange(dataState, "pricing", name, value, handleChange)
                              )}
                              onSubmit={handleSubmitWithValidation}
                              submitted={submitted}/>
      </FormDropdown>
    </>
  );
}
/***/
